import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { API_BASE, getAuthHeaders } from '../services/api';

const PERSONALITY_EMOJIS = {
  warm: '☀️',
  calm: '🌊',
  playful: '🎈',
  professional: '💼',
  friendly: '😊',
  energetic: '⚡',
};

const GENDER_EMOJIS = {
  female: '👩',
  male: '👨',
  neutral: '🧑',
};

function VoiceTile({ voice, isSelected, onClick, compact = false }) {
  const personalityEmoji = PERSONALITY_EMOJIS[voice.personality] || '🎤';
  const genderEmoji = GENDER_EMOJIS[voice.gender] || '';
  
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
      style={{
        padding: compact ? '10px 12px' : '14px 16px',
        borderRadius: compact ? 12 : 16,
        border: isSelected 
          ? '2px solid rgba(167,139,250,0.8)' 
          : '1px solid rgba(255,255,255,0.15)',
        background: isSelected 
          ? 'rgba(167,139,250,0.2)' 
          : 'rgba(255,255,255,0.08)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        textAlign: 'left',
        transition: 'all 0.2s',
        width: '100%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <span style={{ fontSize: compact ? 18 : 22 }}>{personalityEmoji}</span>
        <span style={{ 
          fontSize: compact ? 14 : 16, 
          fontWeight: 600,
          color: 'rgba(255,255,255,0.95)',
          flex: 1,
        }}>
          {voice.name}
        </span>
        {isSelected && (
          <span style={{ fontSize: 14, color: 'rgba(167,139,250,1)' }}>✓</span>
        )}
      </div>
      
      {voice.description && !compact && (
        <span style={{ 
          fontSize: 12, 
          color: 'rgba(255,255,255,0.5)',
          lineHeight: 1.3,
        }}>
          {voice.description}
        </span>
      )}
      
      <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
        {voice.personality && (
          <span style={{
            fontSize: 10,
            padding: '2px 6px',
            borderRadius: 6,
            background: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.6)',
          }}>
            {voice.personality}
          </span>
        )}
        {voice.gender && (
          <span style={{
            fontSize: 10,
            padding: '2px 6px',
            borderRadius: 6,
            background: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.6)',
          }}>
            {genderEmoji} {voice.gender}
          </span>
        )}
      </div>
    </motion.button>
  );
}

export default function VoicePicker({ 
  language = 'en',
  value, 
  onChange, 
  compact = false,
  showLanguageFilter = false,
}) {
  const [voices, setVoices] = useState([]);
  const [byLanguage, setByLanguage] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    loadVoices();
  }, []);

  useEffect(() => {
    setSelectedLanguage(language);
  }, [language]);

  const loadVoices = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/u/voices`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load voices');
      const data = await res.json();
      setVoices(data.voices || []);
      setByLanguage(data.byLanguage || {});
    } catch (err) {
      console.error('Failed to load voices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (voice) => {
    if (onChange) {
      onChange(voice.voiceId, voice);
    }
  };

  const playPreview = async (voice) => {
    if (voice.sampleUrl) {
      setPlaying(voice.voiceId);
      const audio = new Audio(voice.sampleUrl);
      audio.onended = () => setPlaying(null);
      audio.onerror = () => setPlaying(null);
      await audio.play().catch(() => setPlaying(null));
    }
  };

  if (loading) {
    return (
      <div style={{ 
        padding: 20, 
        textAlign: 'center', 
        color: 'rgba(255,255,255,0.5)' 
      }}>
        Loading voices...
      </div>
    );
  }

  const displayVoices = byLanguage[selectedLanguage] || [];
  const languages = Object.keys(byLanguage);

  return (
    <div style={{ width: '100%' }}>
      {showLanguageFilter && languages.length > 1 && (
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}>
          {languages.map(lang => {
            const langVoices = byLanguage[lang] || [];
            const langName = langVoices[0]?.languageName || lang;
            return (
              <button
                key={lang}
                onClick={() => setSelectedLanguage(lang)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 8,
                  border: 'none',
                  background: selectedLanguage === lang 
                    ? 'rgba(167,139,250,0.3)' 
                    : 'rgba(255,255,255,0.1)',
                  color: selectedLanguage === lang 
                    ? 'rgba(167,139,250,1)' 
                    : 'rgba(255,255,255,0.6)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {langName}
              </button>
            );
          })}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: compact ? 8 : 12,
      }}>
        {displayVoices.map(voice => (
          <VoiceTile
            key={voice.voiceId}
            voice={voice}
            isSelected={value === voice.voiceId}
            onClick={() => handleSelect(voice)}
            compact={compact}
          />
        ))}
      </div>

      {displayVoices.length === 0 && (
        <p style={{ 
          textAlign: 'center', 
          color: 'rgba(255,255,255,0.4)',
          padding: 20,
        }}>
          No voices available for this language
        </p>
      )}
    </div>
  );
}

export { VoiceTile };
